/**
 * Where the Journal destination gets its repository, and how the library is
 * prepared (F-JR-41, F-JR-43).
 *
 * `AppPorts` (P05) registers the *platform* adapters — clock, ticker,
 * visibility, export, key-value. It declares no storage ports, so there is no
 * composition root that hands a surface a `JournalRepositoryPort`. Rather than
 * reach into storage from a component, this module keeps the same shape the
 * port registry uses: one context carrying the repository, replaced wholesale
 * in a test by a provider holding `@ff/domain`'s fake.
 *
 * Unlike the effort stores, there is **no lazily constructed browser default**.
 * `@ff/store`'s `createJournalRepository` needs a `JournalFileStore`, and the
 * only implementation in this workspace is `MemoryJournalFileStore` — a test
 * substrate whose whole purpose is to fail on command. The OPFS-backed store is
 * not built yet. Defaulting to the memory one would ship a journal that
 * silently forgets everything on reload, which is a worse failure than saying
 * so: with no repository the library reports **unavailable** through F-JR-03's
 * own copy, which is exactly what that state is for and which no user can
 * mistake for "your writing is gone". The gap is named in the packet report.
 *
 * The read itself is fail-closed **per record**, which is the Journal's
 * distinguishing storage property: one entry whose bytes will not decode must
 * not close the Journal, so `JournalLibraryRead` carries what it could read
 * *and* what it quarantined, and this loader propagates both rather than
 * letting either stand in for the other.
 */

import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo } from "react";
import type {
  JournalDraft,
  JournalEntry,
  JournalGoal,
  JournalGoalId,
  JournalLibraryRead,
  JournalOpenLibrary,
  JournalRecoveryItem,
  JournalRepositoryErrorCode,
} from "@ff/domain";
import { JOURNAL_LIBRARY_COPY, journalStandaloneDrafts } from "@ff/domain";
import type { JournalRepositoryPort } from "@ff/domain/ports";

import type { JournalLibraryLoad } from "../../../privacy";
import { journalProjections } from "./projection";

/**
 * The repository, or its explicit absence.
 *
 * `null` is a value the surface branches on rather than a hole it trips over:
 * "this build cannot open the protected journal" is a real state with real
 * copy, and it is not the same as "the repository said no".
 */
export type JournalRepositoryHandle = JournalRepositoryPort | null;

const JournalRepositoryContext = createContext<JournalRepositoryHandle>(null);

export interface JournalRepositoryProviderProps {
  readonly repository: JournalRepositoryHandle;
  readonly children?: ReactNode;
}

export function JournalRepositoryProvider({
  repository,
  children,
}: JournalRepositoryProviderProps): ReactNode {
  return (
    <JournalRepositoryContext.Provider value={repository}>
      {children}
    </JournalRepositoryContext.Provider>
  );
}

export function useJournalRepository(): JournalRepositoryHandle {
  return useContext(JournalRepositoryContext);
}

/**
 * F-JR-03's mapped messages.
 *
 * Three of the repository's error codes have their own sentence because they
 * say different things to the person reading them; everything else gets the
 * default, which is deliberately vague about *which* step failed rather than
 * guessing and putting a wrong sentence in front of a user. Every one of them
 * ends by saying nothing was replaced, because that is the property the
 * fail-closed write path actually guarantees.
 */
export function journalUnavailableMessage(
  error: JournalRepositoryErrorCode | undefined,
): string {
  switch (error) {
    case "repositoryUnavailable":
      return JOURNAL_LIBRARY_COPY.repositoryUnavailable;
    case "enumerationFailed":
      return JOURNAL_LIBRARY_COPY.enumerationFailed;
    case undefined:
      return JOURNAL_LIBRARY_COPY.unavailableFallback;
    default:
      return JOURNAL_LIBRARY_COPY.defaultUnavailable;
  }
}

/**
 * Reads entries, drafts, goals and quarantine, and projects them.
 *
 * The three reads run concurrently because they are independent files and the
 * surface is blocked on all three; they are *awaited together* rather than
 * sequenced so a slow goal library cannot delay the timeline.
 *
 * The notice is set whenever anything was quarantined, and only then. Saying
 * "some journal data needs recovery" over a clean library would teach the user
 * to ignore the one sentence that means their bytes are being preserved.
 */
export async function loadJournalLibrary(
  repository: JournalRepositoryHandle,
): Promise<JournalLibraryLoad> {
  if (repository === null) {
    return {
      kind: "failed",
      message: JOURNAL_LIBRARY_COPY.unavailableFallback,
      recoveries: [],
    };
  }

  const availability = await repository.availability();
  if (availability.status === "unavailable") {
    // Recoveries are metadata — id, kind, reason, byte count, digest — never
    // decoded content, so they are safe to surface from a repository that
    // cannot serve entries, and surfacing them is what lets a user act on
    // corruption instead of concluding their journal is gone.
    const recoveries = await repository.recoveries().catch(() => [] as JournalRecoveryItem[]);
    return {
      kind: "failed",
      message: journalUnavailableMessage(availability.error),
      recoveries,
    };
  }

  const [entries, drafts, goals, quarantined]: [
    JournalLibraryRead<JournalEntry>,
    JournalLibraryRead<JournalDraft>,
    JournalLibraryRead<JournalGoal>,
    readonly JournalRecoveryItem[],
  ] = await Promise.all([
    repository.readEntries(),
    repository.readDrafts(),
    repository.readGoals(),
    repository.recoveries(),
  ]);

  const recoveries = dedupeRecoveries([
    ...quarantined,
    ...entries.recoveries,
    ...drafts.recoveries,
    ...goals.recoveries,
  ]);

  // A read that reports an availability error did not finish enumerating, so
  // its `items` are a *partial* library. Rendering them as the timeline would
  // present a subset of the user's writing as the whole of it, which is the one
  // failure mode the per-record quarantine exists to avoid — so the whole load
  // fails, with the recoveries still surfaced.
  const availabilityError =
    entries.availabilityError ?? drafts.availabilityError ?? goals.availabilityError;
  if (availabilityError !== undefined) {
    return {
      kind: "failed",
      message: journalUnavailableMessage(availabilityError),
      recoveries,
    };
  }

  const library: JournalOpenLibrary = {
    projections: journalProjections(entries.items, drafts.items),
    standaloneDrafts: standaloneDrafts(drafts.items, entries.items),
    goals: goals.items,
    recoveries,
    ...(recoveries.length === 0 ? {} : { notice: JOURNAL_LIBRARY_COPY.needsRecovery }),
  };

  return { kind: "loaded", library };
}

/**
 * F-JR-05: drafts whose id is **not** among the persisted entry ids, newest
 * `savedAt` first, ties by id ascending.
 *
 * `journalStandaloneDrafts` in `@ff/domain` owns the rule; this wrapper only
 * builds the id set, which is the part a caller would otherwise rebuild at each
 * call site out of whichever entry list happened to be at hand.
 */
function standaloneDrafts(
  drafts: readonly JournalDraft[],
  entries: readonly JournalEntry[],
): readonly JournalDraft[] {
  return journalStandaloneDrafts(drafts, new Set(entries.map((entry) => entry.id)));
}

/**
 * One row per preserved file, keyed by `presentationID` = "kind|path|sha256".
 *
 * The repository's own `recoveries()` and each library read can both report the
 * same quarantined file; showing it twice would suggest two damaged copies
 * where there is one.
 */
function dedupeRecoveries(
  items: readonly JournalRecoveryItem[],
): readonly JournalRecoveryItem[] {
  const seen = new Map<string, JournalRecoveryItem>();
  for (const item of items) {
    const key = `${item.kind}|${item.path}|${item.contentSHA256}`;
    if (!seen.has(key)) seen.set(key, item);
  }
  return [...seen.values()];
}

/**
 * The loader the shared privacy session is constructed with.
 *
 * Handed *to* the privacy layer rather than called by it directly: the
 * repository is P17's, and a privacy layer that reached for it itself would be
 * a privacy layer that could be made to read while concealed.
 */
export function useJournalLibraryLoader(): () => Promise<JournalLibraryLoad> {
  const repository = useJournalRepository();
  return useCallback(() => loadJournalLibrary(repository), [repository]);
}

/** The live goals, by id — the map the preview chain's `why` link needs. */
export function useJournalGoalIndex(
  goals: readonly JournalGoal[],
): ReadonlyMap<JournalGoalId, JournalGoal> {
  return useMemo(() => new Map(goals.map((goal) => [goal.id, goal])), [goals]);
}
